import createReReSelector from 're-reselect';
import { createSelector } from 'reselect';

import {
    getBannerLangDropdownId,
    getBannerLangDropdownItems,
    getBannerLangDropdownLabel,
    LanguageDropdownParams
} from '../../service/BannersLang/BannersLang';
import {
    getMetrikaContersDependableFieldsWarningText,
    METRIKA_COUNTERS_DEPENDABLE_FIELDS
} from '../../service/CampaignsEditHelpers/CampaignsEditHelpers';
import {
    EditCampaignData,
    EditCampaignDataValue,
    EditCampaignsConstants,
    EditCampaignValueField,
    EnabledFieldsMap
} from '../../typings/campaignsEdit';
import { ValidationErrorData } from '../../typings/common';
import { Goal } from '../../typings/goals';
import { GdContentLanguage } from '../../typings/graphql-grid';
import { GdBidModifierType } from '../../typings/graphql-grid';
import { MetrikaCounterEditorValue } from '../../typings/metrikaCounterEditor';
import { NotificationsValue } from '../../typings/notificationEditor';
import { RetargetingConditionDataForRateCorrections } from '../../typings/ratesCorrections';
import { GoalsDataState, State } from '../../typings/state';
import { TimeZone, TimeZoneGroup } from '../../typings/timeTargetingEditor';
import { VisitingCardValueEditCampaign } from '../../typings/VisitingCardEditor';

export const getCampaign = (
    state: State,
    campaignId: string
): EditCampaignData => state.editCampaigns.campaigns[campaignId];

export const getCampaignValue = (
    state: State,
    campaignId: string
): EditCampaignDataValue => state.editCampaigns.campaigns[campaignId].value;

export const getCampaignData = (
    state: State,
    campaignId: string
): EditCampaignData => {
    return state.editCampaigns.campaigns[campaignId];
};

export const selectEnabledFieldsForCampaign = createReReSelector(
    getCampaignData,
    (campaignData: EditCampaignData): EnabledFieldsMap => {
        return campaignData.enabledFields;
    }
)((state: State, campaignId: string) => campaignId);

export const selectRetargetingConditionsDataForEditCampaigns = createSelector(
    (state: State) => state.editCampaigns.retargetingConditions,
    (
        retargetingConditionsData?: Array<
            RetargetingConditionDataForRateCorrections
        >
    ): Array<RetargetingConditionDataForRateCorrections> => {
        return retargetingConditionsData || [];
    }
);

export const selectCampaignDisabledIpsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): string[] => {
        return campaignValue[EditCampaignValueField.disabledIps] || [];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignBannedDomainsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): string[] => {
        return campaignValue[EditCampaignValueField.bannedDomains] || [];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignAllowedPageIdsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): string[] => {
        return campaignValue[EditCampaignValueField.allowedPageIds] || [];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignNotificationsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): NotificationsValue => {
        return campaignValue[EditCampaignValueField.notifications];
    }
)((state: State, campaignId: string) => campaignId);

export const selectMetrikaGoals = createSelector(
    (state: State) => state.goalsData,
    (goalsData: GoalsDataState): Record<string, Goal> => {
        if (goalsData === undefined || Object.keys(goalsData).length === 0) {
            return {};
        }

        return goalsData.goals || {};
    }
);

export const selectCampaignBannersLanguageValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): GdContentLanguage | undefined => {
        return campaignValue[EditCampaignValueField.bannersLanguage];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignBannersLanguageErrors = createReReSelector(
    getCampaign,
    (campaignValue: EditCampaignData): ValidationErrorData[] | undefined => {
        return campaignValue.errors[EditCampaignValueField.bannersLanguage];
    }
)((state: State, campaignId: string) => campaignId);

export const selectBannersLanguageDropdownParams = createSelector(
    selectCampaignBannersLanguageValue,
    selectCampaignBannersLanguageErrors,
    (
        selectedLanguage?: GdContentLanguage,
        errors?: ValidationErrorData[]
    ): LanguageDropdownParams => {
        return {
            selectedId: getBannerLangDropdownId(selectedLanguage),
            label: getBannerLangDropdownLabel(selectedLanguage),
            items: getBannerLangDropdownItems(),
            errors
        };
    }
);

export const selectMetrikaCounterEditorValueFromCampaignValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): MetrikaCounterEditorValue => {
        return {
            counters: campaignValue[EditCampaignValueField.metrikaCounters],
            markMetrikaLinks:
                campaignValue[EditCampaignValueField.markMetrikaLinks]
        };
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignMetrikaCountersDependableFields = createReReSelector(
    getCampaign,
    (campaign: EditCampaignData): EditCampaignValueField[] => {
        const enabledFieldsForCampaign = campaign.enabledFields;

        return METRIKA_COUNTERS_DEPENDABLE_FIELDS.filter(
            field => enabledFieldsForCampaign[field]
        );
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignMetrikaCountersWarningMessage = createReReSelector(
    selectMetrikaCounterEditorValueFromCampaignValue,
    selectCampaignMetrikaCountersDependableFields,
    (
        metrikaCounterEditorValue: MetrikaCounterEditorValue,
        dependableFields: EditCampaignValueField[]
    ): string | undefined => {
        return metrikaCounterEditorValue &&
            metrikaCounterEditorValue.counters.length !== 0
            ? getMetrikaContersDependableFieldsWarningText(dependableFields)
            : undefined;
    }
)((state: State, campaignId: string) => campaignId);

export const selectTimeZoneGroups = createSelector(
    (state: State) => state.commonData.timeZoneGroups,
    (timeZoneGroups: TimeZoneGroup[]) => timeZoneGroups
);

export const selectCountryTimeZone = createSelector(
    selectTimeZoneGroups,
    (timeZoneGroups?: TimeZoneGroup[]): TimeZone[] | undefined => {
        if (timeZoneGroups === undefined) {
            return undefined;
        }

        return timeZoneGroups.map(timeZoneGroup => {
            return {
                id: timeZoneGroup.id,
                name: timeZoneGroup.name
            };
        });
    }
);

export const selectTimeZoneMenuItems = createReReSelector(
    (state: State, idTimeZoneGroup: string) => idTimeZoneGroup,
    (state: State, idTimeZoneGroup: string) => selectTimeZoneGroups(state),
    (
        idTimeZoneGroup: string,
        timeZoneGroups?: TimeZoneGroup[]
    ): TimeZone[] | undefined => {
        if (timeZoneGroups === undefined) {
            return undefined;
        }

        const currentTimeZoneGroup = timeZoneGroups.find(
            timeZoneGroup => timeZoneGroup.id === idTimeZoneGroup
        );

        if (currentTimeZoneGroup === undefined) {
            return undefined;
        }

        return currentTimeZoneGroup.timezones.map(timezone => {
            return {
                id: timezone.id,
                name: timezone.name
            };
        });
    }
)((state: State, idTimeZoneGroup: string) => idTimeZoneGroup);

export const selectSelectedCountryTimeZone = createReReSelector(
    (state: State, idTimeZoneGroup: string) => idTimeZoneGroup,
    (state: State, idTimeZoneGroup: string) => selectCountryTimeZone(state),
    (
        idTimeZoneGroup: string,
        countryTimeZoneMenuItems?: TimeZone[]
    ): TimeZone | undefined => {
        if (countryTimeZoneMenuItems === undefined) {
            return undefined;
        }

        return countryTimeZoneMenuItems.find(
            countryItem => countryItem.id === idTimeZoneGroup
        );
    }
)((state: State, idTimeZoneGroup: string) => idTimeZoneGroup);

export const selectSelectedTimeZone = createReReSelector(
    (state: State, idTimeZoneGroup: string, idTimeZone: string) => idTimeZone,
    (state: State, idTimeZoneGroup: string, idTimeZone: string) =>
        selectTimeZoneMenuItems(state, idTimeZoneGroup),
    (
        idTimeZone: string,
        timeZoneMenuItems?: TimeZone[]
    ): TimeZone | undefined => {
        if (timeZoneMenuItems === undefined) {
            return undefined;
        }

        return timeZoneMenuItems.find(item => item.id === idTimeZone);
    }
)(
    (state: State, idTimeZoneGroup: string, idTimeZone: string) =>
        idTimeZoneGroup + idTimeZone
);

export const selectCampaignVisitingCard = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): VisitingCardValueEditCampaign => {
        return campaignValue[EditCampaignValueField.visitingCard];
    }
)((state: State, campaignId: string) => campaignId);

export const selectEditCampaignsConstants = createSelector(
    (state: State) => state.editCampaigns.constants,
    (constants?: EditCampaignsConstants): EditCampaignsConstants => {
        if (constants === undefined) {
            throw new Error('Константы редактирования кампаний не существуют');
        }

        return constants;
    }
);

export const selectEnabledRateCorrectionsForCampaign = (
    state: State,
    campaignId: string
): Array<GdBidModifierType> => {
    const campaignType = state.editCampaigns.campaigns[campaignId].type;

    return (
        (campaignType &&
            state.editCampaigns.constants &&
            state.editCampaigns.constants.allowedRateCorrectionsData[
                campaignType
            ]) ||
        []
    );
};
